import {
  formatAmount,
  formatInvoiceDate,
  InvoiceLayout,
  OrderDetailBatchReturnType,
  useInvoiceRecipientAddress,
} from './common';

const DUBAI_VAT_RATE = 0.05;

const extractTaxFromGross = (grossAmount: number) =>
  Math.round((grossAmount * DUBAI_VAT_RATE) / (1 + DUBAI_VAT_RATE));

const DubaiInvoiceHeader = () => {
  return (
    <header className="mb-4 text-center">
      <p className="text-sm font-bold mb-1">
        XIAOMA MEA HANDBAGS &amp; LEATHER PRODUCTS TRADING L.L.C
      </p>
      <p className="text-xs mb-1">TRN NO. 104838181600001</p>
      <p className="text-xs mb-3">
        THE BINARY TOWER, UNIT 102, BUSINESS BAY DUBAI, UAE
      </p>
      <hr className="border-0 border-t border-black mb-3" />
      <p className="text-xl font-bold tracking-wide">TAX INVOICE</p>
    </header>
  );
};

const DubaiInvoiceFooter = () => {
  return (
    <section className="text-xs leading-[1.8]">
      <p>
        <span className="font-bold">BANK DETAIL:</span> DUBAI ISLAMIC BANK
      </p>
      <p>
        <span className="font-bold">Bank Account Name:</span> XIAOMA MEA
        HANDBAGS AED LE
      </p>
      <p>
        <span className="font-bold">Bank Account Number:</span> 097524161250901
      </p>
      <p>
        <span className="font-bold">IBAN Number:</span> AE930240097524161250901
      </p>
    </section>
  );
};

const DubaiInvoiceItemsTable = ({
  order,
}: {
  order: OrderDetailBatchReturnType;
}) => {
  return (
    <table className="w-full border-collapse text-xs mb-4">
      <thead>
        <tr>
          <th className="border border-black w-[72px] p-1.5 text-center font-bold">
            No.
          </th>
          <th className="border border-black p-1.5 text-center font-bold">
            Description
          </th>
          <th className="border border-black w-[80px] p-1.5 text-center font-bold">
            Quantity
          </th>
          <th className="border border-black w-[200px] p-1.5 text-center font-bold whitespace-nowrap">
            Total Amount Incl VAT
          </th>
        </tr>
      </thead>
      <tbody>
        {order.orderItems.map((item) => {
          const quantity = item.productQuantity || 1;
          const totalInclVat =
            (item.productPriceActualCurrency || 0) * quantity;

          return (
            <tr key={item.id}>
              <td className="border border-black p-1.5 text-center align-middle">
                {item.productId}
              </td>
              <td className="border border-black px-2 py-1.5 align-middle whitespace-pre-wrap">
                {item.productName}
              </td>
              <td className="border border-black p-1.5 text-center align-middle tabular-nums">
                {quantity}
              </td>
              <td className="border border-black p-1.5 text-right align-middle tabular-nums">
                {formatAmount(totalInclVat)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const DubaiInvoice = ({ order }: { order: OrderDetailBatchReturnType }) => {
  const { cityLabel, countryLabel } = useInvoiceRecipientAddress(order);
  const totalAfterVat = order.payAmountActualCurrency || 0;
  const actualCurrencyRate = order.orderItems[0]?.actualCurrencyRate ?? 1;
  const integrationDeduction = order.useIntegration
    ? Math.round(order.useIntegration * actualCurrencyRate)
    : 0;
  const freightAmount = order.freightAmountActualCurrency || 0;
  const discountAmount = order.promotionAmountActualCurrency || 0;

  const originalItemsGross = order.orderItems.reduce(
    (sum, item) =>
      sum +
      (item.productPriceActualCurrency || 0) * (item.productQuantity || 1),
    0,
  );

  // 汇总 VAT：优惠后税基（与后端 / 订单消费税额一致）
  const taxBase = Math.max(0, originalItemsGross - discountAmount);
  const vatAmount = order.totalTaxAmount ?? extractTaxFromGross(taxBase);
  const totalBeforeVat = totalAfterVat - vatAmount;

  const summaryRows = [
    freightAmount
      ? {
          label: 'Freight:',
          value: formatAmount(freightAmount),
        }
      : null,
    discountAmount
      ? {
          label: 'Discount:',
          value: `-${formatAmount(discountAmount)}`,
        }
      : null,
    integrationDeduction
      ? {
          label: 'Points Deduction:',
          value: `-${formatAmount(integrationDeduction)}`,
        }
      : null,
    {
      label: 'Total Before Vat:',
      value: formatAmount(totalBeforeVat),
    },
    {
      label: 'VAT 5%:',
      value: formatAmount(vatAmount),
    },
    {
      label: 'Total After VAT:',
      value: formatAmount(totalAfterVat),
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row));

  return (
    <InvoiceLayout>
      <div className="font-mono text-black">
        <DubaiInvoiceHeader />

        <section className="mb-5 flex items-start justify-between gap-6 text-xs leading-[1.8]">
          <div className="min-w-0 flex-1">
            <div className="font-bold mb-2">TO :</div>
            <div>
              {order.receiverName}
              {order.receiverDetailAddress ? (
                <>
                  <br />
                  {order.receiverDetailAddress}
                </>
              ) : null}
              {cityLabel || countryLabel ? (
                <>
                  <br />
                  {cityLabel} {countryLabel}
                </>
              ) : null}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <p>
              <span className="font-bold">Invoice Date:</span>{' '}
              {formatInvoiceDate(order, 'D. M. YYYY')}
            </p>
            <p>
              <span className="font-bold">Invoice No:</span> {order.invoiceNo}
            </p>
          </div>
        </section>

        <DubaiInvoiceItemsTable order={order} />

        <section className="ml-auto mb-8 w-[300px] text-xs leading-[1.8]">
          <div className="grid grid-cols-[1fr_96px] gap-x-4">
            {summaryRows.map((row) => (
              <div className="contents" key={row.label}>
                <span className="text-right font-bold">{row.label}</span>
                <span className="text-right tabular-nums">{row.value}</span>
              </div>
            ))}
          </div>
        </section>

        <DubaiInvoiceFooter />
      </div>
    </InvoiceLayout>
  );
};

export default DubaiInvoice;
